const money = (value) => `$${Number(value || 0).toFixed(2)}`;

export const cartCompanies = (activeOrder) => {
    const companies = activeOrder?.companies;
    if (Array.isArray(companies)) return companies;
    if (companies && typeof companies === 'object') return Object.values(companies);
    return [];
};

export const findCompanyCart = (activeOrder, companyRuc) =>
    cartCompanies(activeOrder).find(
        (company) => String(company?.company || company?.ruc || '') === String(companyRuc || '')
    ) || null;

const companyDisplayName = (companiesByRuc, companyRuc, fallback = '') => (
    fallback
    || companiesByRuc?.[companyRuc]?.tradeName
    || companiesByRuc?.[companyRuc]?.name
    || (companyRuc ? String(companyRuc) : '')
);

export const hasCompanyDirectory = (companiesByRuc) => (
    Boolean(companiesByRuc)
    && typeof companiesByRuc === 'object'
    && Object.keys(companiesByRuc).length > 0
);

export const offersFreeShipping = (policy) => policy?.type === 'FREE_OVER_AMOUNT';

export const productCompanyRuc = (product) => {
    if (!product) return '';
    if (typeof product.company === 'string') return product.company;
    if (product.company?.ruc) return String(product.company.ruc);
    if (product.companyRuc) return String(product.companyRuc);
    return '';
};

/**
 * Live policy from the company directory. A loaded directory wins over a cart
 * snapshot. An empty directory means the list has not arrived yet.
 */
export const liveShippingPolicy = (companiesByRuc, companyRuc) => {
    if (!hasCompanyDirectory(companiesByRuc)) return null;
    const company = companiesByRuc?.[companyRuc];
    if (company?.shippingPolicy?.type) return company.shippingPolicy;
    return { type: 'NO_FREE_SHIPPING' };
};

export const companyOffersFreeShipping = (companiesByRuc, companyRuc) => (
    offersFreeShipping(liveShippingPolicy(companiesByRuc, companyRuc))
);

const policyForProgress = ({ companiesByRuc, companyRuc, policy }) => {
    const livePolicy = liveShippingPolicy(companiesByRuc, companyRuc);
    if (livePolicy?.type) return livePolicy;
    return policy || null;
};

export const listShippingProgress = ({
    activeOrder = null,
    companiesByRuc = {},
    companyRuc = null,
    policy = null,
    companyName = '',
} = {}) => {
    const entries = [];

    if (companyRuc) {
        const companyCart = findCompanyCart(activeOrder, companyRuc);
        const view = shippingProgressView({
            companyCart,
            policy: policyForProgress({ companiesByRuc, companyRuc, policy }),
        });
        if (view) {
            entries.push({
                key: String(companyRuc),
                name: companyDisplayName(companiesByRuc, companyRuc, companyName),
                ...view,
            });
        }
        return entries;
    }

    cartCompanies(activeOrder).forEach((companyCart) => {
        const ruc = companyCart?.company || companyCart?.ruc;
        if (!ruc) return;
        const view = shippingProgressView({
            companyCart,
            policy: policyForProgress({ companiesByRuc, companyRuc: ruc }),
        });
        if (!view) return;
        entries.push({
            key: String(ruc),
            name: companyDisplayName(companiesByRuc, ruc),
            ...view,
        });
    });

    return entries;
};

export const compactShippingProgress = (entries = []) => {
    if (!Array.isArray(entries) || entries.length === 0) {
        return null;
    }

    const pending = entries.filter((entry) => !(entry.granted || Number(entry.remaining) <= 0));
    if (pending.length === 0) {
        return {
            label: 'Envío gratis alcanzado',
            progress: 100,
            reached: true,
            entry: entries[0],
        };
    }

    const next = pending.reduce((closest, entry) => (
        Number(entry.remaining) < Number(closest.remaining) ? entry : closest
    ));
    const place = entries.length > 1 && next.name ? ` en ${next.name}` : '';

    return {
        label: `Faltan ${money(next.remaining)}${place}`,
        progress: next.progress,
        reached: false,
        entry: next,
    };
};

const resolvedPolicy = (companyCart, policy) => {
    if (policy?.type) return policy;
    if (companyCart?.shippingPolicySnapshot?.type) {
        return companyCart.shippingPolicySnapshot;
    }
    return null;
};

/**
 * Progress toward free shipping for one company sub-order.
 * The API already counts every product toward freeShippingThresholdSubtotal.
 * Free shipping, once granted, still applies only to eligible products.
 */
export const shippingProgressView = ({ companyCart = null, policy = null } = {}) => {
    const shippingPolicy = resolvedPolicy(companyCart, policy);
    if (shippingPolicy?.type !== 'FREE_OVER_AMOUNT') {
        return null;
    }

    const threshold = Number(shippingPolicy.freeShippingThreshold);
    if (!Number.isFinite(threshold)) {
        return null;
    }

    const rawSubtotal = Number(companyCart?.freeShippingThresholdSubtotal);
    const subtotal = Number.isFinite(rawSubtotal) ? rawSubtotal : 0;
    const snapshot = companyCart?.shippingPolicySnapshot;
    const snapshotThreshold = Number(snapshot?.freeShippingThreshold);
    const sameOffer = !policy?.type || (
        snapshot?.type === 'FREE_OVER_AMOUNT'
        && Number.isFinite(snapshotThreshold)
        && snapshotThreshold === threshold
    );
    const rawRemaining = companyCart?.freeShippingRemaining;
    const remaining = !sameOffer || rawRemaining === null || rawRemaining === undefined || rawRemaining === ''
        ? Number(Math.max(0, threshold - subtotal).toFixed(2))
        : Number(rawRemaining);
    const safeRemaining = Number.isFinite(remaining) ? Math.max(0, remaining) : 0;
    const granted = sameOffer && typeof companyCart?.freeShippingGranted === 'boolean'
        ? companyCart.freeShippingGranted
        : safeRemaining <= 0;
    const progress = granted || threshold <= 0
        ? 100
        : Math.min(100, Math.round((subtotal / threshold) * 100));

    return {
        threshold,
        subtotal,
        remaining: granted ? 0 : safeRemaining,
        granted,
        progress,
    };
};

export const isShippingPending = ({ shippingPrice, address } = {}) => {
    const amount = Number(shippingPrice);
    if (Number.isFinite(amount) && amount > 0) {
        return false;
    }

    return !address?.address1;
};

export const shippingSummaryText = ({ shippingPrice, pending = false } = {}) => {
    if (pending) {
        return 'Pendiente por calcular';
    }

    const amount = Number(shippingPrice);
    if (!Number.isFinite(amount) || amount <= 0) {
        return 'Gratis';
    }

    return money(amount);
};

export const describeShippingProgress = ({
    remaining = 0,
    granted = false,
    progress = 0,
    companyName = '',
    includeCompanyName = false,
} = {}) => {
    const place = includeCompanyName && companyName ? ` en ${companyName}` : '';
    const reached = granted || Number(remaining) <= 0;

    if (reached) {
        return {
            title: 'Envío gratis',
            message: `Ya tienes envío gratis${place}. Aplica solo en algunos productos; el resto paga envío.`,
            progress: 100,
        };
    }

    return {
        title: 'Envío gratis',
        message: `Te faltan ${money(remaining)} para envío gratis${place}. Aplica solo en algunos productos.`,
        progress,
    };
};
